import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import {
  POSTINGS_DASHBOARD_ASSIGNMENT_SUMMARY_INCLUDES,
  POSTINGS_SORT_API_ENUMS,
  POSTINGS_NETWORK_FILTER_API_ENUMS,
  POSTINGS_COMPANY_TEAM_FILTER_OPTIONS,
  POSTINGS_PER_PAGE
} from "../../../constants/routes/postings";
import { ICON_SORTS } from "../../../constants/lsg";

export default Service.extend({
  /**
   * Register services
   */
  store: service(),
  errorTracker: service('error-tracker'),
  assignmentCommandService : service('commands/assignment-commands'),
  state: service('states/routes/postings'),

  /**
   * Decrement one page of results if possible.
   *
   * @param {String} sort             of type POSTINGS_SORT_API_ENUMS.*
   * @param {String} networkFilter    of type POSTINGS_NETWORK_FILTER_API_ENUMS.*
   * @param {Number|null} companyTeamFilter of type CompanyTeam.id
   * @param {String} secondarySort    of type ICON_SORTS.*
   * @return {*|Ember.RSVP.Promise}
   */
  decrementPage(sort, networkFilter, companyTeamFilter, secondarySort) {
    let pagination = this.get('state.currentPagination'),
      newPageNumber = pagination.get('currentPage') > 1 ? pagination.get('currentPage') - 1 : 1,
      resultsPerPage = pagination.get('perPage');

    this.setNotFirstQuery();

    return this.loadAssignmentDashboardList(
      this.get('state.currentAssignmentStatus'),
      newPageNumber,
      resultsPerPage,
      sort,
      networkFilter,
      companyTeamFilter,
      secondarySort
    );
  },

  /**
   * Increment one page of results if possible.
   *
   * @param {String} sort             of type POSTINGS_SORT_API_ENUMS.*
   * @param {String} networkFilter    of type POSTINGS_NETWORK_FILTER_API_ENUMS.*
   * @param {Number|null} companyTeamFilter of type CompanyTeam.id
   * @param {String} secondarySort    of type ICON_SORTS.*
   * @return {*|Ember.RSVP.Promise}
   */
  incrementPage(sort, networkFilter, companyTeamFilter, secondarySort) {
    let pagination = this.get('state.currentPagination'),
      newPageNumber = pagination.get('currentPage') + 1,
      resultsPerPage = pagination.get('perPage');

    this.setNotFirstQuery();

    return this.loadAssignmentDashboardList(
      this.get('state.currentAssignmentStatus'),
      newPageNumber,
      resultsPerPage,
      sort,
      networkFilter,
      companyTeamFilter,
      secondarySort
    );
  },

  /**
   * Load the list of AssignmentDashboardList for the view.
   *
   * @param {String}  status              of type Assignment.STATUS_*
   * @param {Number=} opt_page            The current page number we'd like to fetch
   * @param {Number=} opt_perPageCount    The count of how many items we want per page.
   * @param {String=} opt_sort            of type POSTINGS_SORT_API_ENUMS.*
   * @param {String=} opt_networkFilter   of type POSTINGS_NETWORK_FILTER_API_ENUMS.*
   * @param {String=} opt_companyTeamFilter of type POSTINGS_COMPANY_TEAM_FILTER_OPTIONS.*
   * @param {String=} opt_secondarySort   of type ICON_SORTS.*
   *
   * @return {Ember.RSVP.Promise}
   */
  loadAssignmentDashboardList(
  status,
  opt_page,
  opt_perPageCount,
  opt_sort,
  opt_networkFilter,
  opt_companyTeamFilter,
  opt_secondarySort) {
    let page          = 1,
      resultsPerPage  = opt_perPageCount || POSTINGS_PER_PAGE,
      sort            = opt_sort || POSTINGS_SORT_API_ENUMS.LAST_ACTIVITY,
      networkFilter   = opt_networkFilter || POSTINGS_NETWORK_FILTER_API_ENUMS.POSTED_BY_ANYONE,
      companyTeamFilter = opt_companyTeamFilter || null,
      secondarySort   = opt_secondarySort || ICON_SORTS.SORT_DESCENDING,
      pageToResultsMap = this.get('state.pageToResultsMap'),
      cacheKey        = this.get('state.currentCacheKey');

    if (opt_page) {
      page = opt_page;
    } else if (this.get('state.currentPagination')) {
      page = this.get('state.currentPagination.currentPage');
    }

    //check our cache
    if (pageToResultsMap[page]) {
      this.set('state.assignmentDashboardList', pageToResultsMap[page]);
      return Promise.resolve(pageToResultsMap[page]);
    }

    this.set('state.isLoadingResult', true);
    this.set('state.currentAssignmentStatus', status);
    this.set('state.networkFilterSelectedValue', networkFilter);
    this.set('state.companyTeamFilterSelectedValue', companyTeamFilter);
    this.set('state.secondarySort', secondarySort);

    //not in cache, let's call the DB.
    return this.get('assignmentCommandService').loadAssignmentDashboardListRequest(
      status,
      sort,
      networkFilter,
      companyTeamFilter,
      secondarySort,
      page,
      resultsPerPage,
      cacheKey,
      POSTINGS_DASHBOARD_ASSIGNMENT_SUMMARY_INCLUDES)
      .then((dashboardList) => {
        //store this result in the cache
        this.get('state.pageToResultsMap')[page] = dashboardList;

        this.updateDashboardListState(dashboardList);
      })
      .catch(e => this.get('errorTracker').error('loadAssignmentDashboardList - args - ' + arguments.toString() + e.toString(), this))
      .finally(() => {
        if (!this.get('state.isSearchActivated')) {
          this.set('state.isLoadingResult', false);
        }
      });
  },

  /**
   * Reset the search state
   */
  resetSearch() {
    this.set('state.isSearchActivated', false);
  },

  /**
   * Reset the state manager to defaults.
   *
   * @param {Boolean=} opt_isResetFirstQuery   Set this to true to reset the isFirstQuery flag
   */
  resetStateManager(opt_isResetFirstQuery) {
    this.toggleIsStateResetting(true);
    this.state.initializeDefaults();
    if (opt_isResetFirstQuery) {
      this.set('state.isFirstQuery', true);
    }
    this.toggleIsStateResetting(false);
  },

  /**
   * Given a string text value, search the database for the assignment summaries containing that.
   *
   * @param {String} textValue
   * @return {Ember.RSVP.Promise}
   */
  searchAssignmentDashboardListForText(textValue) {
    this.set('state.isLoadingResult', true);

    return this.assignmentCommandService.searchDashboardList(
      textValue,
      POSTINGS_DASHBOARD_ASSIGNMENT_SUMMARY_INCLUDES
    ).then((dashboardList) => {
      if (this.get('state.isSearchActivated')) {
        this.set('state.assignmentDashboardList', dashboardList);
      }
    }).finally(() => {
      if (this.get('state.isSearchActivated')) {
        this.set('state.isLoadingResult', false);
      }
    });
  },

  /**
   * Set the isFirstQuery flag to false
   */
  setNotFirstQuery() {
    this.set('state.isFirstQuery', false);
  },

  /**
   * Swap our previous results cache with the active results list
   */
  swapCacheToDashboardList() {
    this.set('state.isLoadingResult', false);

    let cachedResult = this.get('state.assignmentDashboardListWhileSearchingCache');
    if (cachedResult) {
      this.set('state.assignmentDashboardList', cachedResult);
      this.set('state.assignmentDashboardListWhileSearchingCache', null);
    }
  },

  /**
   * Put active results list into the cache.
   */
  swapDashboardListToCache() {
    this.set('state.isLoadingResult', false);

    if (this.get('state.assignmentDashboardList')) {
      this.set('state.assignmentDashboardListWhileSearchingCache', this.get('state.assignmentDashboardList'));
      this.set('state.assignmentDashboardList', null);
    }
  },

  /**
   * Toggle the stateIsResetting boolean
   *
   * @param {Boolean} isResetting
   */
  toggleIsStateResetting(isResetting) {
    this.set('state.isResettingState', isResetting);
  },

  /**
   * Given the dashboard list, update the state correctly based on our search state.
   *
   * @param {AssignmentDashboardList} dashboardList
   */
  updateDashboardListState(dashboardList) {
    if (!this.get('state.isSearchActivated')) {
      //update state.
      this.set('state.assignmentDashboardList', dashboardList);
    } else {
      this.set('state.assignmentDashboardListWhileSearchingCache', dashboardList);
    }
  }
});
